import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { Sparkles, Shuffle, Download, Loader2, LogOut, User as UserIcon } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = () => {
  const handleLogin = () => {
    // Direct Google OAuth via our backend
    window.location.href = `${API}/auth/login`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFFDF5] to-[#FFF0F0] flex flex-col items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center max-w-md"
      >
        <h1 className="text-6xl md:text-7xl font-bold text-[#2D2D2D] mb-4">
          🐱 PurrfectGen
        </h1>
        <p className="text-xl text-[#5F5F5F] mb-8">
          Generate amazing AI-powered cat photos
        </p>
        <button
          data-testid="google-login-button"
          onClick={handleLogin}
          className="bg-white text-[#2D2D2D] border-2 border-[#E0E0E0] hover:border-[#FF6B6B] rounded-full px-8 py-4 font-bold shadow-lg hover:shadow-xl transition-all flex items-center gap-3 mx-auto"
        >
          <svg width="20" height="20" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Sign in with Google
        </button>
      </motion.div>
    </div>
  );
};

const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true
        });
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
        navigate('/login');
      }
    };

    checkAuth();
  }, [navigate]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#FFFDF5] to-[#FFF0F0]">
        <Loader2 className="animate-spin text-[#FF6B6B]" size={48} />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return children({ user });
};

const Home = ({ user }) => {
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [cats, setCats] = useState([]);
  const [latestCat, setLatestCat] = useState(null);
  const [loadingGallery, setLoadingGallery] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCats();
  }, []);

  const fetchCats = async () => {
    try {
      const response = await axios.get(`${API}/cats`, {
        withCredentials: true
      });
      setCats(response.data);
      setLoadingGallery(false);
    } catch (error) {
      console.error("Error fetching cats:", error);
      setLoadingGallery(false);
    }
  };

  const triggerConfetti = () => {
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#FF6B6B', '#4D96FF', '#FFD93D']
    });
  };

  const generateCat = async () => {
    if (!prompt.trim()) {
      alert("Please enter a description for your cat!");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/generate-cat`,
        { prompt: prompt },
        { withCredentials: true }
      );
      
      setLatestCat(response.data);
      setCats([response.data, ...cats]);
      triggerConfetti();
      setPrompt("");
    } catch (error) {
      console.error("Error generating cat:", error);
      alert("Failed to generate cat. Please try again!");
    } finally {
      setLoading(false);
    }
  };

  const generateRandomCat = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/random-cat`,
        {},
        { withCredentials: true }
      );
      
      setLatestCat(response.data);
      setCats([response.data, ...cats]);
      triggerConfetti();
    } catch (error) {
      console.error("Error generating random cat:", error);
      alert("Failed to generate random cat. Please try again!");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(
        `${API}/auth/logout`,
        {},
        { withCredentials: true }
      );
      navigate('/login');
    } catch (error) {
      console.error("Logout error:", error);
      navigate('/login');
    }
  };

  const downloadImage = (imageData, prompt, id) => {
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${imageData}`;
    link.download = `cat-${id}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !loading) {
      generateCat();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFFDF5] to-[#FFF0F0]">
      {/* Header */}
      <motion.header 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="sticky top-0 z-50 backdrop-blur-md bg-white/60 border-b-2 border-[#F0F0F0] shadow-sm"
      >
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="text-3xl md:text-4xl font-bold text-[#2D2D2D]">
            🐱 PurrfectGen
          </h1>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              {user.picture ? (
                <img 
                  src={user.picture} 
                  alt={user.name}
                  className="w-10 h-10 rounded-full border-2 border-[#FF6B6B]"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-[#FF6B6B] flex items-center justify-center">
                  <UserIcon className="text-white" size={20} />
                </div>
              )}
              <span className="text-[#2D2D2D] font-medium hidden md:block">{user.name}</span>
            </div>
            <button
              data-testid="logout-button"
              onClick={handleLogout}
              className="bg-white text-[#5F5F5F] hover:text-[#FF6B6B] border-2 border-[#E0E0E0] hover:border-[#FF6B6B] rounded-full px-4 py-2 font-medium transition-colors flex items-center gap-2"
            >
              <LogOut size={18} />
              <span className="hidden md:inline">Logout</span>
            </button>
          </div>
        </div>
      </motion.header>

      {/* Hero Section */}
      <section className="container mx-auto px-6 py-12 md:py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-center mb-12"
        >
          <h2 className="text-5xl md:text-7xl font-bold text-[#2D2D2D] mb-6 tracking-tight">
            Generate Amazing
            <br />
            <span className="text-[#FF6B6B]">Cat Photos</span> ✨
          </h2>
          <p className="text-lg md:text-xl text-[#5F5F5F] max-w-2xl mx-auto">
            Describe your dream cat or let AI surprise you with a random feline friend!
          </p>
        </motion.div>

        {/* Generator Controls */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4 }}
          className="max-w-4xl mx-auto mb-16"
        >
          <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] p-6 md:p-8 border-2 border-[#F0F0F0]">
            <div className="flex flex-col md:flex-row gap-4">
              <input
                data-testid="cat-prompt-input"
                type="text"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Describe your cat... (e.g., 'a fluffy orange cat reading a book')"
                disabled={loading}
                className="flex-1 bg-[#FFFDF5] border-2 border-[#E0E0E0] focus:border-[#FF6B6B] focus:ring-4 focus:ring-[#FF6B6B]/20 rounded-full px-8 py-5 text-lg transition-all placeholder:text-gray-400 outline-none"
              />
              <button
                data-testid="generate-cat-button"
                onClick={generateCat}
                disabled={loading}
                className="bg-[#FF6B6B] text-white hover:bg-[#FF5252] disabled:bg-gray-300 disabled:cursor-not-allowed rounded-full px-8 py-5 font-bold shadow-[0_4px_0_0_#D64545] active:shadow-none active:translate-y-[4px] transition-all flex items-center justify-center gap-2 whitespace-nowrap"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" size={20} />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles size={20} />
                    Generate Cat
                  </>
                )}
              </button>
            </div>
            
            <div className="mt-6 flex justify-center">
              <button
                data-testid="random-cat-button"
                onClick={generateRandomCat}
                disabled={loading}
                className="bg-white text-[#4D96FF] border-2 border-[#4D96FF] hover:bg-[#F0F7FF] disabled:opacity-50 disabled:cursor-not-allowed rounded-full px-6 py-3 font-bold shadow-sm transition-colors flex items-center gap-2"
              >
                <Shuffle size={18} />
                Surprise Me!
              </button>
            </div>
          </div>
        </motion.div>

        {/* Latest Generated Cat */}
        <AnimatePresence>
          {latestCat && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="max-w-3xl mx-auto mb-16"
            >
              <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] overflow-hidden border-2 border-[#F0F0F0]">
                <div className="cat-image-container aspect-square">
                  <img
                    src={`data:image/png;base64,${latestCat.image_data}`}
                    alt={latestCat.prompt}
                    className="w-full h-full object-cover"
                  />
                  <div className="download-overlay">
                    <button
                      data-testid="download-latest-cat-button"
                      onClick={() => downloadImage(latestCat.image_data, latestCat.prompt, latestCat.id)}
                      className="bg-white text-[#2D2D2D] rounded-full px-6 py-3 font-bold flex items-center gap-2 hover:scale-110 transition-transform"
                    >
                      <Download size={20} />
                      Download
                    </button>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-[#5F5F5F] text-center italic">"{latestCat.prompt}"</p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Gallery Section */}
        <div className="mt-20">
          <motion.h3
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-4xl md:text-5xl font-bold text-[#2D2D2D] text-center mb-12"
          >
            Your Gallery 🎨
          </motion.h3>

          {loadingGallery ? (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="animate-spin text-[#FF6B6B]" size={48} />
            </div>
          ) : cats.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-2xl text-[#5F5F5F]">No cats yet! Generate your first one above 👆</p>
            </div>
          ) : (
            <motion.div
              initial="hidden"
              animate="show"
              variants={{
                show: {
                  transition: {
                    staggerChildren: 0.1
                  }
                }
              }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            >
              {cats.map((cat, index) => (
                <motion.div
                  key={cat.id}
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    show: { opacity: 1, y: 0 }
                  }}
                  whileHover={{ scale: 1.03 }}
                  className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)] overflow-hidden border-2 border-[#F0F0F0] transition-all"
                >
                  <div className="cat-image-container aspect-square">
                    <img
                      src={`data:image/png;base64,${cat.image_data}`}
                      alt={cat.prompt}
                      className="w-full h-full object-cover"
                    />
                    <div className="download-overlay">
                      <button
                        data-testid={`download-cat-button-${index}`}
                        onClick={() => downloadImage(cat.image_data, cat.prompt, cat.id)}
                        className="bg-white text-[#2D2D2D] rounded-full p-3 hover:scale-110 transition-transform"
                        aria-label="Download cat image"
                      >
                        <Download size={20} />
                      </button>
                    </div>
                  </div>
                  <div className="p-4">
                    <p className="text-sm text-[#5F5F5F] line-clamp-2">{cat.prompt}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t-2 border-[#F0F0F0] mt-20">
        <div className="container mx-auto px-6 py-8 text-center">
          <p className="text-[#5F5F5F]">
            Made with 💖 by PurrfectGen | Powered by Gemini AI
          </p>
        </div>
      </footer>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route 
            path="/" 
            element={
              <ProtectedRoute>
                {({ user }) => <Home user={user} />}
              </ProtectedRoute>
            } 
          />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;